/**
 * Shape SVG Path Generator & Formatter Utilities
 */

export function getStarSvgPath(
  width: number,
  height: number,
  pointsCount: number = 5,
  innerRatio: number = 0.4,
  cornerRadius: number = 0
): string {
  const n = Math.max(3, pointsCount || 5);
  const outerR = Math.min(width || 40, height || 40) / 2;
  const innerR = outerR * (innerRatio !== undefined ? innerRatio : 0.4);
  const cx = (width || 40) / 2;
  const cy = (height || 40) / 2;
  const pts: { x: number; y: number }[] = [];
  let angle = -Math.PI / 2;
  const step = Math.PI / n;

  for (let i = 0; i < 2 * n; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const px = cx + r * Math.cos(angle);
    const py = cy + r * Math.sin(angle);
    pts.push({ x: px, y: py });
    angle += step;
  }

  const cr = Math.max(0, cornerRadius || 0);
  if (cr <= 0) {
    return `M ${pts.map(p => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' L ')} Z`;
  }

  // Draw smooth rounded corners for star vertices
  const numPts = pts.length;
  let d = '';
  for (let i = 0; i < numPts; i++) {
    const prev = pts[(i - 1 + numPts) % numPts];
    const curr = pts[i];
    const next = pts[(i + 1) % numPts];

    const v1 = { x: prev.x - curr.x, y: prev.y - curr.y };
    const v2 = { x: next.x - curr.x, y: next.y - curr.y };
    const len1 = Math.hypot(v1.x, v1.y) || 1;
    const len2 = Math.hypot(v2.x, v2.y) || 1;

    const r = Math.min(cr, len1 / 2, len2 / 2);
    const startX = curr.x + (v1.x / len1) * r;
    const startY = curr.y + (v1.y / len1) * r;
    const endX = curr.x + (v2.x / len2) * r;
    const endY = curr.y + (v2.y / len2) * r;

    if (i === 0) {
      d += `M ${startX.toFixed(2)} ${startY.toFixed(2)}`;
    } else {
      d += ` L ${startX.toFixed(2)} ${startY.toFixed(2)}`;
    }
    d += ` Q ${curr.x.toFixed(2)} ${curr.y.toFixed(2)}, ${endX.toFixed(2)} ${endY.toFixed(2)}`;
  }
  return d + ' Z';
}

export function getPolygonSvgPath(
  width: number,
  height: number,
  sidesCount: number = 6,
  cornerRadius: number = 0
): string {
  const n = Math.max(3, sidesCount || 6);
  const r = Math.min(width || 40, height || 40) / 2;
  const cx = (width || 40) / 2;
  const cy = (height || 40) / 2;
  const pts: { x: number; y: number }[] = [];
  let angle = -Math.PI / 2;
  const step = (2 * Math.PI) / n;

  for (let i = 0; i < n; i++) {
    const px = cx + r * Math.cos(angle);
    const py = cy + r * Math.sin(angle);
    pts.push({ x: px, y: py });
    angle += step;
  }

  const cr = Math.max(0, cornerRadius || 0);
  if (cr <= 0) {
    return `M ${pts.map(p => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' L ')} Z`;
  }

  // Draw smooth rounded corners for polygon
  const numPts = pts.length;
  let d = '';
  for (let i = 0; i < numPts; i++) {
    const prev = pts[(i - 1 + numPts) % numPts];
    const curr = pts[i];
    const next = pts[(i + 1) % numPts];

    const v1 = { x: prev.x - curr.x, y: prev.y - curr.y };
    const v2 = { x: next.x - curr.x, y: next.y - curr.y };
    const len1 = Math.hypot(v1.x, v1.y) || 1;
    const len2 = Math.hypot(v2.x, v2.y) || 1;

    const rad = Math.min(cr, len1 / 2, len2 / 2);
    const startX = curr.x + (v1.x / len1) * rad;
    const startY = curr.y + (v1.y / len1) * rad;
    const endX = curr.x + (v2.x / len2) * rad;
    const endY = curr.y + (v2.y / len2) * rad;

    if (i === 0) {
      d += `M ${startX.toFixed(2)} ${startY.toFixed(2)}`;
    } else {
      d += ` L ${startX.toFixed(2)} ${startY.toFixed(2)}`;
    }
    d += ` Q ${curr.x.toFixed(2)} ${curr.y.toFixed(2)}, ${endX.toFixed(2)} ${endY.toFixed(2)}`;
  }
  return d + ' Z';
}

export function getLineEffectSvgPath(
  width: number,
  height: number,
  lineEffect: string = 'none',
  amplitudeParam?: number,
  frequencyParam?: number,
  pointsStyleParam?: 'corner' | 'smooth'
): string {
  const w = Math.max(10, width || 100);
  const h = Math.max(10, height || 20);
  const midY = h / 2;

  const amplitude = amplitudeParam !== undefined && !isNaN(amplitudeParam) ? amplitudeParam : Math.min(midY - 1, 10);
  const segs = Math.max(1, frequencyParam !== undefined && !isNaN(frequencyParam) ? frequencyParam : 8);
  const isSmoothPoints = pointsStyleParam === 'smooth';

  switch (lineEffect) {
    case 'arc':
    case 'smooth': {
      return `M 0 ${midY} Q ${(w / 2).toFixed(1)} ${(midY - amplitude * 2.5).toFixed(1)}, ${w} ${midY}`;
    }
    case 'arc_lower': {
      return `M 0 ${midY} Q ${(w / 2).toFixed(1)} ${(midY + amplitude * 2.5).toFixed(1)}, ${w} ${midY}`;
    }
    case 'arc_upper': {
      return `M 0 ${(midY + amplitude).toFixed(1)} Q ${(w / 2).toFixed(1)} ${(midY - amplitude * 2).toFixed(1)}, ${w} ${(midY + amplitude).toFixed(1)}`;
    }
    case 'arch': {
      const leg = Math.min(w * 0.2, 20);
      return `M 0 ${midY} L ${leg.toFixed(1)} ${midY} Q ${(w / 2).toFixed(1)} ${(midY - amplitude * 2.5).toFixed(1)}, ${(w - leg).toFixed(1)} ${midY} L ${w} ${midY}`;
    }
    case 'bulge': {
      return `M 0 ${midY} C ${(w * 0.3).toFixed(1)} ${(midY - amplitude * 2).toFixed(1)}, ${(w * 0.7).toFixed(1)} ${(midY - amplitude * 2).toFixed(1)}, ${w} ${midY}`;
    }
    case 'shell_lower': {
      return `M 0 ${(midY - amplitude).toFixed(1)} Q ${(w * 0.5).toFixed(1)} ${(midY + amplitude * 2).toFixed(1)}, ${w} ${(midY - amplitude).toFixed(1)}`;
    }
    case 'shell_upper': {
      return `M 0 ${(midY + amplitude).toFixed(1)} Q ${(w * 0.5).toFixed(1)} ${(midY - amplitude * 2).toFixed(1)}, ${w} ${(midY + amplitude).toFixed(1)}`;
    }
    case 'flag': {
      return `M 0 ${midY} C ${(w * 0.33).toFixed(1)} ${(midY - amplitude * 2).toFixed(1)}, ${(w * 0.66).toFixed(1)} ${(midY + amplitude * 2).toFixed(1)}, ${w} ${midY}`;
    }
    case 'wave':
    case 'sine': {
      const stepX = w / segs;
      let d = `M 0 ${midY}`;
      if (pointsStyleParam === 'corner') {
        for (let i = 1; i <= segs; i++) {
          const x = i * stepX;
          const y = i % 2 === 1 ? midY - amplitude : midY + amplitude;
          d += ` L ${x.toFixed(1)} ${y.toFixed(1)}`;
        }
      } else {
        for (let i = 0; i < segs; i++) {
          const x1 = i * stepX + stepX / 2;
          const y1 = i % 2 === 0 ? midY - amplitude : midY + amplitude;
          const x2 = (i + 1) * stepX;
          const y2 = midY;
          d += ` Q ${x1.toFixed(1)} ${y1.toFixed(1)}, ${x2.toFixed(1)} ${y2.toFixed(1)}`;
        }
      }
      return d;
    }
    case 'fish': {
      return `M 0 ${midY} C ${(w * 0.4).toFixed(1)} ${(midY - amplitude * 2.5).toFixed(1)}, ${(w * 0.8).toFixed(1)} ${(midY - amplitude * 0.5).toFixed(1)}, ${w} ${midY}`;
    }
    case 'rise': {
      return `M 0 ${(midY + amplitude).toFixed(1)} Q ${(w * 0.5).toFixed(1)} ${(midY).toFixed(1)}, ${w} ${(midY - amplitude * 2).toFixed(1)}`;
    }
    case 'fisheye': {
      return `M 0 ${midY} C ${(w * 0.25).toFixed(1)} ${(midY - amplitude * 2.5).toFixed(1)}, ${(w * 0.75).toFixed(1)} ${(midY - amplitude * 2.5).toFixed(1)}, ${w} ${midY}`;
    }
    case 'inflate': {
      return `M 0 ${midY} C ${(w * 0.3).toFixed(1)} ${(midY - amplitude * 2).toFixed(1)}, ${(w * 0.7).toFixed(1)} ${(midY - amplitude * 2).toFixed(1)}, ${w} ${midY}`;
    }
    case 'squeeze': {
      return `M 0 ${midY} Q ${(w * 0.5).toFixed(1)} ${(midY + amplitude * 1.5).toFixed(1)}, ${w} ${midY}`;
    }
    case 'zigzag': {
      const stepX = w / segs;
      let d = `M 0 ${midY}`;
      if (isSmoothPoints) {
        for (let i = 0; i < segs; i++) {
          const x1 = i * stepX + stepX / 2;
          const y1 = i % 2 === 0 ? midY - amplitude : midY + amplitude;
          const x2 = (i + 1) * stepX;
          const y2 = midY;
          d += ` Q ${x1.toFixed(1)} ${y1.toFixed(1)}, ${x2.toFixed(1)} ${y2.toFixed(1)}`;
        }
      } else {
        for (let i = 1; i <= segs; i++) {
          const x = i * stepX;
          const y = i % 2 === 1 ? midY - amplitude : midY + amplitude;
          d += ` L ${x.toFixed(1)} ${y.toFixed(1)}`;
        }
      }
      return d;
    }
    case 'twist':
    case 'loop': {
      const stepX = w / segs;
      let d = `M 0 ${midY}`;
      for (let i = 0; i < segs; i++) {
        const startX = i * stepX;
        const endX = (i + 1) * stepX;
        d += ` C ${(startX + stepX * 0.3).toFixed(1)} ${midY - amplitude * 1.5}, ${(endX - stepX * 0.3).toFixed(1)} ${midY + amplitude * 1.5}, ${endX.toFixed(1)} ${midY}`;
      }
      return d;
    }
    default:
      return `M 0 ${midY} L ${w} ${midY}`;
  }
}

export function formatCurrencyValue(
  val: any, 
  symbol: string = '₹',
  decimalPlaces: number = 2,
  formatThousands: boolean = true
): string {
  const sym = symbol || '₹';
  if (val === undefined || val === null || val === '') return `${sym} ${Number(0).toFixed(decimalPlaces)}`;
  const numStr = String(val).replace(/[^0-9.-]+/g, '');
  const num = parseFloat(numStr);
  if (isNaN(num)) return `${sym} ${val}`;
  
  if (formatThousands) {
    return `${sym} ${num.toLocaleString('en-IN', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    })}`;
  }
  return `${sym} ${num.toFixed(decimalPlaces)}`;
}

export function parseDateFlexible(val: any): Date | null {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  const str = String(val).trim();

  // Match YYYY-MM-DD or YYYY/MM/DD
  const ymd = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(str);
  if (ymd) {
    return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  }

  // Match DD/MM/YYYY or MM/DD/YYYY or DD-MM-YYYY
  const dmy = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(str);
  if (dmy) {
    const p1 = Number(dmy[1]);
    const p2 = Number(dmy[2]);
    const p3 = Number(dmy[3]);
    if (p1 > 12) {
      // First part is > 12, so it's DD/MM/YYYY
      return new Date(p3, p2 - 1, p1);
    }
    // Default assume DD/MM/YYYY
    return new Date(p3, p2 - 1, p1);
  }

  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

export function getLocalIsoDateString(d: Date = new Date()): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDateValue(val: any, formatPattern: string = 'DD/MM/YYYY'): string {
  if (!val) return '';
  const d = parseDateFlexible(val) || new Date(val);
  if (isNaN(d.getTime())) return String(val);

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();

  const monthNamesShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthNamesLong = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const mShort = monthNamesShort[d.getMonth()];
  const mLong = monthNamesLong[d.getMonth()];

  switch (formatPattern) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'DD MMM YYYY':
      return `${day} ${mShort} ${year}`;
    case 'MMM DD, YYYY':
      return `${mShort} ${day}, ${year}`;
    case 'DD MMMM YYYY':
      return `${day} ${mLong} ${year}`;
    case 'YYYY/MM/DD':
      return `${year}/${month}/${day}`;
    default:
      return `${day}/${month}/${year}`;
  }
}

export function getTriangleSvgPath(width: number, height: number): string {
  const w = Math.max(1, width || 40);
  const h = Math.max(1, height || 40);
  return `M ${w / 2} 0 L ${w} ${h} L 0 ${h} Z`;
}

export function getDiamondSvgPath(width: number, height: number): string {
  const w = Math.max(1, width || 40);
  const h = Math.max(1, height || 40);
  return `M ${w / 2} 0 L ${w} ${h / 2} L ${w / 2} ${h} L 0 ${h / 2} Z`;
}

export function getHeartSvgPath(width: number, height: number): string {
  const w = Math.max(1, width || 40);
  const h = Math.max(1, height || 40);
  const topH = h * 0.3;
  return `M ${w / 2} ${h} C 0 ${h / 2}, 0 0, ${w / 2} ${topH} C ${w} 0, ${w} ${h / 2}, ${w / 2} ${h} Z`;
}

export function getArrowSvgPath(width: number, height: number): string {
  const w = Math.max(1, width || 40);
  const h = Math.max(1, height || 40);
  const headW = w * 0.4;
  const shaftH = h * 0.4;
  const shaftTop = (h - shaftH) / 2;
  const shaftBottom = shaftTop + shaftH;
  const shaftEnd = w - headW;
  return `M 0 ${shaftTop} L ${shaftEnd} ${shaftTop} L ${shaftEnd} 0 L ${w} ${h / 2} L ${shaftEnd} ${h} L ${shaftEnd} ${shaftBottom} L 0 ${shaftBottom} Z`;
}

export function getRoundedRectSvgPath(width: number, height: number, radius: number): string {
  const w = Math.max(1, width || 40);
  const h = Math.max(1, height || 40);
  const r = Math.max(0, Math.min(radius || 0, w / 2, h / 2));
  if (r <= 0) return `M 0 0 L ${w} 0 L ${w} ${h} L 0 ${h} Z`;
  return `M ${r} 0 L ${w - r} 0 Q ${w} 0 ${w} ${r} L ${w} ${h - r} Q ${w} ${h} ${w - r} ${h} L ${r} ${h} Q 0 ${h} 0 ${h - r} L 0 ${r} Q 0 0 ${r} 0 Z`;
}
